import { useState } from 'react';
import PropTypes from 'prop-types';
import { Paneset, Loading, Layout } from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';
import ViewReportBuilder from '../../components/reports/builders/shared/ViewReportBuilder';
import ResultsPreview from '../../components/reports/results/preview/ResultsPreview';
import SaveNewReportManager from '../../components/reports/management/save/SaveNewReportManager';
import useURLParams from '../../hooks/useURLParams';
import useCubeQuery from '../../hooks/useQuery';
import useReportContext from '../../hooks/useReportContext';

// ── Section config for cataloging_view ────────────────────────────────────
const CATALOGING_SECTIONS = [
  {
    formField:   'instanceFields',
    filtersField: 'instanceFilters',
    fieldType:   'dimension',
    label: (
      <FormattedMessage
        id="ui-reports.catalogingReports.instanceFields.title"
        defaultMessage="Bibliographic Data (Instances)"
      />
    ),
    description: (
      <FormattedMessage
        id="ui-reports.catalogingReports.instanceFields.description"
        defaultMessage="Select fields and filters for bibliographic data:"
      />
    ),
    fields: [
      'cataloging_view.title',
      'cataloging_view.instance_hrid',
      'cataloging_view.contributors',
      'cataloging_view.identifiers',
      'cataloging_view.subjects',
      'cataloging_view.languages',
      'cataloging_view.series_list',
      'cataloging_view.editions_list',
      'cataloging_view.publication_info',
      'cataloging_view.publicationrange',
      'cataloging_view.publicationfrequency',
      'cataloging_view.classifications',
      'cataloging_view.nature_of_content_list',
      'cataloging_view.physicaldescriptions',
      'cataloging_view.instance_formats_list',
      'cataloging_view.instance_format_name',
      'cataloging_view.electronic_access_urls',
      'cataloging_view.alternative_titles_list',
      'cataloging_view.statistical_codes_list',
      'cataloging_view.indextitle',
      'cataloging_view.source',
      'cataloging_view.tags',
      'cataloging_view.resource_type',
      'cataloging_view.resource_status',
      'cataloging_view.mode_of_issuance_name',
      'cataloging_view.suppression_status',
      'cataloging_view.discoverysuppress',
      'cataloging_view.staffsuppress',
      'cataloging_view.instance_notes',
      'cataloging_view.instance_admin_notes',
      'cataloging_view.instance_created_date',
      'cataloging_view.instance_updated_date',
      'cataloging_view.catalogeddate',
    ],
  },
  {
    formField:   'holdingsFields',
    filtersField: 'holdingsFilters',
    fieldType:   'dimension',
    label: (
      <FormattedMessage
        id="ui-reports.catalogingReports.holdingsFields.title"
        defaultMessage="Holdings Data"
      />
    ),
    description: (
      <FormattedMessage
        id="ui-reports.catalogingReports.holdingsFields.description"
        defaultMessage="Select fields and filters for holdings data:"
      />
    ),
    fields: [
      'cataloging_view.holdings_hrid',
      'cataloging_view.call_number',
      'cataloging_view.call_number_type',
      'cataloging_view.location_name',
      'cataloging_view.library_name',
      'cataloging_view.campus_name',
      'cataloging_view.institution_name',
      'cataloging_view.holdings_notes',
      'cataloging_view.holdings_admin_notes',
      'cataloging_view.holdings_created_date',
      'cataloging_view.holdings_updated_date',
    ],
  },
  {
    formField:   'itemFields',
    filtersField: 'itemFilters',
    fieldType:   'dimension',
    label: (
      <FormattedMessage
        id="ui-reports.catalogingReports.itemFields.title"
        defaultMessage="Item Data"
      />
    ),
    description: (
      <FormattedMessage
        id="ui-reports.catalogingReports.itemFields.description"
        defaultMessage="Select fields and filters for item data:"
      />
    ),
    fields: [
      'cataloging_view.item_hrid',
      'cataloging_view.barcode',
      'cataloging_view.item_status',
      'cataloging_view.material_type',
      'cataloging_view.loan_type',
      'cataloging_view.item_location_name',
      'cataloging_view.item_library_name',
      'cataloging_view.item_campus_name',
      'cataloging_view.item_institution_name',
      'cataloging_view.copy_number',
      'cataloging_view.item_notes',
      'cataloging_view.item_admin_notes',
      'cataloging_view.item_created_date',
      'cataloging_view.item_updated_date',
      'cataloging_view.status_date',
    ],
  },
  {
    formField:   'measures',
    filtersField: 'measuresFilters',
    fieldType:   'measure',
    label: (
      <FormattedMessage
        id="ui-reports.catalogingReports.measures.title"
        defaultMessage="Statistics & Measures"
      />
    ),
    description: (
      <FormattedMessage
        id="ui-reports.catalogingReports.measures.description"
        defaultMessage="Select statistical measures and filters:"
      />
    ),
    fields: [
      'cataloging_view.total_instances',
      'cataloging_view.total_holdings',
      'cataloging_view.total_items',
      'cataloging_view.instances_created',
      'cataloging_view.holdings_created',
      'cataloging_view.items_created',
      'cataloging_view.instances_updated',
      'cataloging_view.holdings_updated',
      'cataloging_view.items_updated',
      'cataloging_view.instances_suppressed',
    ],
  },
];

function CatalogingReportsRoute(props) {
  const [formState, setFormState] = useURLParams('form_state', '');
  const hideBuilder = new URLSearchParams(props.location?.search).get('hide_builder') === 'true';
  const [showBuilderPane, setShowBuilderPane] = useState(!hideBuilder);
  const [showSavePane, setShowSavePane]       = useState(false);
  const [currentSaveMode, setCurrentSaveMode] = useState('new');
  const [currentQuery, setCurrentQuery]       = useState('');
  const [queryOptions, setQueryOptions]       = useState({});
  const [executionCount, setExecutionCount]   = useState(0);
  const [resultSet, error, isLoading]         = useCubeQuery(currentQuery, queryOptions);

  const {
    reportId, reportName, reportDesc,
    isRunMode, isEditMode,
    defaultDisplayMethod: savedDefaultDisplay,
    displayMethods: savedDisplayMethods,
    returnSearch, isContextLoading,
  } = useReportContext(props.location);

  const [displayMethod, setDisplayMethod] = useURLParams('dm', savedDefaultDisplay);
  const isReportMode = (isRunMode || isEditMode) && reportId;
  const hasChanges   = isReportMode && (isEditMode || executionCount > 1);

  const handleClose = () => {
    if (isReportMode && reportId) {
      props.history.push({ pathname: `/reports/reports-list/${reportId}/view`, search: returnSearch || '' });
    } else {
      props.history.push('/reports/report-builder');
    }
  };

  if (isContextLoading) {
    return (
      <Layout className="centered full" style={{ height: '8rem' }}>
        <Loading size="xlarge" />
      </Layout>
    );
  }

  return (
    <Paneset>
      <ViewReportBuilder
        viewName="cataloging_view"
        sections={CATALOGING_SECTIONS}
        timeDimension="cataloging_view.instance_created_date"
        builderTitle={
          <FormattedMessage
            id="ui-reports.catalogingReports.title"
            defaultMessage="Cataloging Report Builder"
          />
        }
        showBuilderPane={showBuilderPane}
        setShowBuilderPane={setShowBuilderPane}
        handleClose={handleClose}
        formState={formState}
        setFormState={setFormState}
        setQuery={(query, options = {}) => {
          setCurrentQuery(query);
          setQueryOptions(options);
          setExecutionCount(prev => prev + 1);
        }}
        isLoading={isLoading}
      />

      <ResultsPreview
        showQueryBuilderPane={showBuilderPane}
        setShowQueryBuilderPane={setShowBuilderPane}
        showSavePane={showSavePane}
        setShowSavePane={setShowSavePane}
        handleClose={handleClose}
        query={currentQuery}
        setQuery={setCurrentQuery}
        resultSet={resultSet}
        error={error}
        isLoading={isLoading}
        reportType="statistical"
        displayMethod={displayMethod}
        setDisplayMethod={setDisplayMethod}
        savedDisplayMethods={savedDisplayMethods}
        reportName={reportName}
        reportDesc={reportDesc}
        reportId={reportId}
        isRunMode={isRunMode || false}
        isEditMode={isEditMode || false}
        hasChanges={hasChanges}
        onOpenSavePane={mode => { setCurrentSaveMode(mode); setShowBuilderPane(false); setShowSavePane(true); }}
        isContextLoading={isContextLoading}
      />

      <SaveNewReportManager
        {...props}
        showQueryBuilderPane={showBuilderPane}
        setShowQueryBuilderPane={setShowBuilderPane}
        showSavePane={showSavePane}
        setShowSavePane={setShowSavePane}
        handleClose={handleClose}
        query={currentQuery}
        reportType="statistical"
        builderType="cataloging"
        builderState={formState}
        saveMode={currentSaveMode}
        savedReportId={reportId}
        savedReportName={reportName}
        savedReportDesc={reportDesc}
      />
    </Paneset>
  );
}

CatalogingReportsRoute.propTypes = {
  history:  PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  location: PropTypes.object,
};

export default CatalogingReportsRoute;
